/**
 * Auras (ออร่า) round a figure: the standing aura of a title, a mastered neigong or a power realm,
 * rage full, meditation and a boss's own. Which aura an entity carries is data (data/auras and the
 * `aura` fields it lists); this file only draws them.
 *
 * Realistic pixel art like the figures: hard half-pixels (the rig's resolution), no smoothing and
 * no soft gradients — light falls off in hard bands, lit warm toward the key light (upper right),
 * cool and dark where it thins. Each look is drawn in three layers, so what circles the body passes
 * behind it and in front of it:
 *   - `ground`: the sigil and pool of light on the ground, at the feet;
 *   - `back`:   rising qi, smoke or flame behind the body, and whatever circles behind it;
 *   - `front`:  whatever circles in front of the body.
 *
 * Cheap enough for a crowd: every look is a loop of `NF` frames painted once (lazily, on first
 * use) into small canvases, and drawing it is one `drawImage` per layer, picked by a continuous
 * phase of time. Nothing is computed per pixel per frame.
 */
import {
  type AuraDef,
  type AuraLook,
  type Entity,
  FELLOWS,
  MEDITATION_AURA,
  MONSTERS,
  NEIGONG,
  neigongMastered,
  RAGE_AURA,
  rankFor,
  REALM_AURAS,
  STAGES,
  TITLE_AURAS,
  TITLES,
  type World,
} from '@nine/shared';
import { canvas, ctx2d } from './pixel';

type G = CanvasRenderingContext2D;
export type AuraLayer = 'ground' | 'back' | 'front';

/** Frames in a look's loop. */
const NF = 16;
/** Canvas pixels per art pixel: the rig's double resolution. */
const RES = 2;

// ── which auras an entity carries ──

let rankMemo = { spent: -1, realm: 0 };
/** The player's power realm (index into `REALMS`), remembered while cultivation spent is unchanged. */
function realmOf(spent: number): number {
  if (rankMemo.spent !== spent) rankMemo = { spent, realm: Math.floor(rankFor(spent).index / STAGES) };
  return rankMemo.realm;
}

const NEIGONG_AURAS = new Map<string, AuraDef | null>();
/** A neigong's mastery aura, grander for a higher scripture tier (ขั้น). */
function neigongAura(id: string): AuraDef | null {
  let a = NEIGONG_AURAS.get(id);
  if (a === undefined) {
    const n = NEIGONG[id];
    NEIGONG_AURAS.set(id, (a = n ? { style: n.aura.style, color: n.aura.color, tier: n.tier } : null));
  }
  return a;
}

/** The standing aura: a title's, else a mastered neigong's, else the power realm's. */
function standing(w: World, e: Entity): AuraDef | null {
  if (e === w.player) {
    const pr = w.progress;
    const titled = TITLES[pr.titleWorn]?.aura ?? TITLE_AURAS[pr.titleWorn];
    if (titled) return titled;
    if (neigongMastered(pr.neigong, pr.activeNeigong)) return neigongAura(pr.activeNeigong);
    return REALM_AURAS[realmOf(pr.cultivationSpent)] ?? null;
  }
  if (e.rival?.fellow) {
    const mastered = FELLOWS[e.rival.fellow]?.mastered;
    return mastered ? neigongAura(mastered) : null;
  }
  if (e.brain) return MONSTERS[e.brain.defId]?.aura ?? null;
  return null;
}

const NONE: readonly AuraDef[] = [];

/** Every aura the entity shows now, the standing one first. */
export function aurasOf(w: World, e: Entity): readonly AuraDef[] {
  if (e.dead) return NONE;
  const stand = standing(w, e);
  const rage = e.maxRage > 0 && e.rage >= e.maxRage;
  if (!rage && !e.meditating) return stand ? [stand] : NONE;
  const out: AuraDef[] = stand ? [stand] : [];
  if (rage) out.push(RAGE_AURA);
  if (e.meditating) out.push(MEDITATION_AURA);
  return out;
}

/** The aura that also rims the figure's silhouette (a title, a neigong, a boss), if any. */
export function auraRim(auras: readonly AuraDef[]): AuraDef | null {
  const a = auras[0];
  return a && a.style !== 'realm' && a.style !== 'rage' && a.style !== 'meditate' ? a : null;
}

/**
 * Draws one layer of the auras at a figure: `x`, `y` are its feet (the ground point for the
 * `ground` layer), `s` its scale, `t` the world time, `seed` (its id) keeps a crowd out of step.
 */
export function drawAuras(
  g: G,
  auras: readonly AuraDef[],
  layer: AuraLayer,
  x: number,
  y: number,
  s: number,
  t: number,
  alpha: number,
  seed = 0,
): void {
  if (!auras.length) return;
  const was = g.globalAlpha;
  for (const def of auras) {
    const look = framesOf(def);
    const ph = t / look.period + ((seed * 0.618) % 1);
    const c = frameOf(look, Math.floor((ph - Math.floor(ph)) * NF) % NF)[layer];
    if (!c) continue;
    const box = layer === 'ground' ? look.gbox : look.bbox;
    g.globalAlpha = was * alpha;
    g.drawImage(
      c,
      Math.round((x - box.ax * s) * RES) / RES,
      Math.round((y - box.ay * s) * RES) / RES,
      (box.w / RES) * s,
      (box.h / RES) * s,
    );
  }
  g.globalAlpha = was;
}

// ── looks ──

type RGB = readonly [number, number, number];
/** A colour and its opacity (0–255). */
type Ink = readonly [number, number, number, number];

type Marks = 'ticks' | 'lotus' | 'taiji' | 'cracks' | 'petals' | 'swords' | 'none';
type Motes = 'rise' | 'ember' | 'fall' | 'blades' | 'mist';

interface Recipe {
  /** Seconds a loop takes. */
  period: number;
  /** Ground: the ring's radius (art px), the pool of light under it, and the marks on it. */
  ring: number;
  pool: number;
  /** A ragged (ink) edge to the pool. */
  ragged?: boolean;
  marks: Marks;
  /** Qi rising behind the body: height and half-width at the feet (art px), scrolls per loop. */
  rise?: { h: number; w: number; k: number };
  /** Things round the body: what, how many, how far out and how high (art px). */
  motes?: { kind: Motes; n: number; r: number; h: number };
  /** The golden bell (ระฆังทอง) closed over the body. */
  bell?: boolean;
  /** The rising field from faint to full: three bands. */
  field: readonly [Ink, Ink, Ink];
  /** Ground: the pool's bands (outer, inner), the ring, and its lit arc and marks. */
  poolInk: readonly [Ink, Ink];
  ringInk: Ink;
  litInk: Ink;
  /** Motes: main and bright. */
  moteInk: readonly [Ink, Ink];
}

const clamp8 = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
const mix = (a: RGB, b: RGB, k: number): RGB => [
  clamp8(a[0] + (b[0] - a[0]) * k),
  clamp8(a[1] + (b[1] - a[1]) * k),
  clamp8(a[2] + (b[2] - a[2]) * k),
];
const ink = (c: RGB, a: number): Ink => [c[0], c[1], c[2], clamp8(a * 255)];
/** Warm key light, cool shadow, and the black of ink and smoke. */
const WARM: RGB = [255, 240, 214];
const COOL: RGB = [56, 66, 104];
const SOOT: RGB = [20, 17, 24];

function rgbOf(s: string): RGB {
  const [r = 255, g = 255, b = 255] = s.split(',').map((v) => Number(v.trim()));
  return [r, g, b];
}

/** Light: shadow cooled, body as dyed, highlight warmed toward the key light. */
function lightTones(c: RGB) {
  return {
    deep: mix(mix(c, [0, 0, 0], 0.35), COOL, 0.3),
    mid: c,
    hi: mix(c, WARM, 0.55),
  };
}

/** Neigong looks grow with the scripture's tier: a wider ring, taller qi, more motes. */
function recipe(def: AuraDef): Recipe {
  const r = baseRecipe(def);
  const grand = drawnAs(def.style) in NEIGONG_LOOKS ? (def.tier ?? 1) - 1 : 0;
  if (grand <= 0) return r;
  return {
    ...r,
    ring: r.ring + 4 * grand,
    rise: r.rise && { ...r.rise, h: r.rise.h + 8 * grand, w: r.rise.w + 2 * grand },
    motes: r.motes && { ...r.motes, n: r.motes.n + 4 * grand, h: r.motes.h + 6 * grand },
  };
}

const NEIGONG_LOOKS = { flame: 1, mist: 1, taiji: 1, bell: 1, blades: 1, petals: 1, ink: 1, bloodstorm: 1 };

/**
 * The looks the aura reference added (data/auras), drawn as the nearest look this file has until
 * the 3D effects exist.
 */
const DRAWN_AS = {
  wind: 'mist',
  thunder: 'bloodstorm',
  moon: 'mist',
  frost: 'blades',
  guardian: 'petals',
} as const;
type Drawn = Exclude<AuraLook, keyof typeof DRAWN_AS>;
const drawnAs = (look: AuraLook): Drawn =>
  look in DRAWN_AS ? DRAWN_AS[look as keyof typeof DRAWN_AS] : (look as Drawn);

function baseRecipe(def: AuraDef): Recipe {
  const c = rgbOf(def.color);
  const { deep, mid, hi } = lightTones(c);
  const tier = def.tier ?? 1;
  // Light that glows: faint deep band, the colour, a warm core.
  const glow: Recipe['field'] = [ink(deep, 0.3), ink(mid, 0.42), ink(hi, 0.58)];
  const faint: Recipe['field'] = [ink(deep, 0.18), ink(mid, 0.26), ink(hi, 0.36)];
  // Smoke that is dark: a rim of the colour round a black heart.
  const smoke: Recipe['field'] = [ink(mid, 0.32), ink(mix(c, SOOT, 0.55), 0.5), ink(SOOT, 0.62)];
  const base = {
    poolInk: [ink(deep, 0.16), ink(mid, 0.2)] as const,
    ringInk: ink(mid, 0.66),
    litInk: ink(hi, 0.9),
    moteInk: [ink(mid, 0.85), ink(hi, 1)] as const,
  };
  const dark = {
    poolInk: [ink(mix(c, SOOT, 0.6), 0.26), ink(SOOT, 0.34)] as const,
    ringInk: ink(mix(c, SOOT, 0.3), 0.7),
    litInk: ink(mix(c, WARM, 0.3), 0.85),
    moteInk: [ink(SOOT, 0.8), ink(mid, 0.9)] as const,
  };
  switch (drawnAs(def.style)) {
    case 'flame':
      return {
        ...base,
        period: 1.2,
        ring: 18,
        pool: 1,
        marks: 'ticks',
        rise: { h: 58, w: 15, k: 2 },
        motes: { kind: 'ember', n: 9, r: 11, h: 54 },
        field: glow,
      };
    case 'mist':
      return {
        ...base,
        period: 5,
        ring: 20,
        pool: 1,
        marks: 'none',
        rise: { h: 40, w: 12, k: 1 },
        motes: { kind: 'mist', n: 8, r: 13, h: 6 },
        field: faint,
      };
    case 'taiji':
      return {
        ...base,
        period: 6,
        ring: 18,
        pool: 0,
        marks: 'taiji',
        rise: { h: 44, w: 10, k: 1 },
        motes: { kind: 'rise', n: 8, r: 12, h: 46 },
        field: faint,
        moteInk: [ink(SOOT, 0.85), ink([246, 244, 238], 1)],
      };
    case 'bell':
      return {
        ...base,
        period: 2.4,
        ring: 18,
        pool: 1,
        marks: 'ticks',
        bell: true,
        motes: { kind: 'rise', n: 7, r: 12, h: 50 },
        field: glow,
      };
    case 'blades':
      return {
        ...base,
        period: 1.8,
        ring: 18,
        pool: 1,
        marks: 'ticks',
        rise: { h: 40, w: 10, k: 1 },
        motes: { kind: 'blades', n: 4, r: 15, h: 20 },
        field: faint,
      };
    case 'petals':
      return {
        ...base,
        period: 4,
        ring: 20,
        pool: 1,
        marks: 'petals',
        rise: { h: 40, w: 11, k: 1 },
        motes: { kind: 'fall', n: 11, r: 15, h: 56 },
        field: faint,
      };
    case 'ink':
      return {
        ...dark,
        period: 3,
        ring: 20,
        pool: 1,
        ragged: true,
        marks: 'none',
        rise: { h: 50, w: 12, k: 1 },
        motes: { kind: 'rise', n: 7, r: 12, h: 50 },
        field: smoke,
      };
    case 'bloodstorm':
      return {
        ...dark,
        period: 1.2,
        ring: 20,
        pool: 1,
        marks: 'cracks',
        rise: { h: 60, w: 14, k: 3 },
        motes: { kind: 'ember', n: 12, r: 12, h: 58 },
        field: [ink(mid, 0.4), ink(mix(c, WARM, 0.25), 0.55), ink(SOOT, 0.6)],
        moteInk: [ink(mid, 0.9), ink(mix(c, WARM, 0.45), 1)],
      };
    case 'swordink':
      // เถาฉางเยี่ย: a wide pool of ink with a ring of swords lying in it, and black smoke.
      return {
        ...dark,
        period: 4,
        ring: 34,
        pool: 1,
        ragged: true,
        marks: 'swords',
        rise: { h: 64, w: 16, k: 1 },
        motes: { kind: 'rise', n: 10, r: 15, h: 62 },
        field: smoke,
        litInk: ink([214, 220, 228], 0.9),
      };
    case 'realm':
      // Grows with the realm: a ring, then qi rising, then a full column of it.
      return {
        ...base,
        period: 5,
        ring: 14 + tier * 2,
        pool: tier >= 2 ? 1 : 0.6,
        marks: 'ticks',
        rise: tier >= 2 ? { h: 26 + tier * 10, w: 8 + tier, k: 1 } : undefined,
        motes: { kind: 'rise', n: 2 + tier * 3, r: 10 + tier, h: 30 + tier * 8 },
        field: tier >= 3 ? glow : faint,
      };
    case 'rage':
      return {
        ...base,
        period: 0.9,
        ring: 0,
        pool: 0.8,
        marks: 'none',
        rise: { h: 32, w: 12, k: 3 },
        motes: { kind: 'ember', n: 8, r: 11, h: 40 },
        field: glow,
        poolInk: [ink(mix(c, SOOT, 0.5), 0.2), ink(mid, 0.18)],
      };
    case 'meditate':
      return {
        ...base,
        period: 4,
        ring: 16,
        pool: 1,
        marks: 'lotus',
        motes: { kind: 'rise', n: 9, r: 11, h: 40 },
        field: faint,
      };
    case 'demon':
      return {
        ...dark,
        period: 2.6,
        ring: 18 + tier * 3,
        pool: 1,
        ragged: true,
        marks: 'cracks',
        rise: { h: 50 + tier * 6, w: 14 + tier, k: 1 },
        motes: { kind: 'ember', n: 8 + tier * 3, r: 13, h: 56 },
        field: smoke,
        moteInk: [ink(mid, 0.9), ink(mix(c, WARM, 0.4), 1)],
      };
  }
}

// ── painting the frames ──

/** A frame's pixels: each pixel keeps the strongest ink laid on it (hard layers, no blending). */
class Buf {
  readonly px: Uint8ClampedArray;
  any = false;
  constructor(
    readonly w: number,
    readonly h: number,
  ) {
    this.px = new Uint8ClampedArray(w * h * 4);
  }
  put(x: number, y: number, c: Ink): void {
    x = Math.round(x);
    y = Math.round(y);
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return;
    const i = (y * this.w + x) * 4;
    if (this.px[i + 3]! > c[3]) return;
    this.px[i] = c[0];
    this.px[i + 1] = c[1];
    this.px[i + 2] = c[2];
    this.px[i + 3] = c[3];
    this.any = true;
  }
  /** Lays ink over whatever is there, even if fainter (marks drawn on a pool). */
  over(x: number, y: number, c: Ink): void {
    x = Math.round(x);
    y = Math.round(y);
    if (x < 0 || y < 0 || x >= this.w || y >= this.h) return;
    const i = (y * this.w + x) * 4;
    this.px[i] = c[0];
    this.px[i + 1] = c[1];
    this.px[i + 2] = c[2];
    this.px[i + 3] = Math.max(c[3], this.px[i + 3]!);
    this.any = true;
  }
  done(): HTMLCanvasElement | null {
    if (!this.any) return null;
    const c = canvas(this.w, this.h);
    const g = ctx2d(c);
    const img = g.createImageData(this.w, this.h);
    img.data.set(this.px);
    g.putImageData(img, 0, 0);
    return c;
  }
}

/** A stable random number in [0, 1) from a few integers. */
function hash(a: number, b = 0, c = 0): number {
  let h =
    Math.imul(a ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(b + 0x27d4eb2f, 0xc2b2ae35) ^ Math.imul(c, 0x165667b1);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  h = Math.imul(h, 0x297a2d39);
  h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}

const smooth = (v: number) => v * v * (3 - 2 * v);
/** Value noise, repeating every `py` cells down y (so a field scrolled by whole periods loops). */
function noise(x: number, y: number, py: number, salt: number): number {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const fx = smooth(x - xi);
  const fy = smooth(y - yi);
  const wrap = (v: number) => ((v % py) + py) % py;
  const y0 = wrap(yi);
  const y1 = wrap(yi + 1);
  const a = hash(xi, y0, salt);
  const b = hash(xi + 1, y0, salt);
  const c = hash(xi, y1, salt);
  const d = hash(xi + 1, y1, salt);
  return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
}

const TAU = Math.PI * 2;

interface Box {
  w: number;
  h: number;
  ax: number;
  ay: number;
}

/** One frame of a look, all three layers (a layer with nothing in it is null). */
interface Frame {
  ground: HTMLCanvasElement | null;
  back: HTMLCanvasElement | null;
  front: HTMLCanvasElement | null;
}

interface Look {
  recipe: Recipe;
  period: number;
  gbox: Box;
  bbox: Box;
  /** Painted on first use, one frame at a time, so a new look never stalls a frame. */
  frames: (Frame | undefined)[];
}

const LOOKS = new Map<string, Look>();

function framesOf(def: AuraDef): Look {
  const key = `${def.style}|${def.color}|${def.tier ?? 1}`;
  let look = LOOKS.get(key);
  if (!look) LOOKS.set(key, (look = layout(recipe(def))));
  return look;
}

function layout(r: Recipe): Look {
  // Ground box: the ring and a margin, in canvas pixels, anchored at the feet.
  const R = Math.max(r.ring, 14);
  const gw = Math.ceil((R * 2 + 8) * RES);
  const gh = Math.ceil((R * 0.84 + 8) * RES);
  const gbox: Box = { w: gw, h: gh, ax: gw / RES / 2, ay: gh / RES / 2 };
  // Body box: wide enough for what circles it, tall enough for what rises.
  const bwArt = Math.max(2 * (r.motes?.r ?? 0), 2 * (r.rise?.w ?? 0), r.bell ? 30 : 0) + 10;
  const bhArt = Math.max(r.rise?.h ?? 0, r.motes ? r.motes.h + r.motes.r * 0.5 : 0, r.bell ? 50 : 0) + 8;
  const bbox: Box = { w: Math.ceil(bwArt * RES), h: Math.ceil(bhArt * RES), ax: bwArt / 2, ay: bhArt - 4 };
  return { recipe: r, period: r.period, gbox, bbox, frames: new Array<Frame | undefined>(NF) };
}

function frameOf(look: Look, f: number): Frame {
  let fr = look.frames[f];
  if (fr) return fr;
  const { recipe: r, gbox, bbox } = look;
  const p = f / NF;
  const gb = new Buf(gbox.w, gbox.h);
  paintGround(gb, r, p, f, gbox);
  const bb = new Buf(bbox.w, bbox.h);
  const fb = new Buf(bbox.w, bbox.h);
  if (r.rise) paintRise(bb, r, p, bbox);
  if (r.bell) paintBell(bb, fb, r, p, bbox);
  if (r.motes) paintMotes(bb, fb, r, p, f, bbox);
  fr = { ground: gb.done(), back: bb.done(), front: fb.done() };
  look.frames[f] = fr;
  return fr;
}

/** Ground: banded pool, the ring with its lit arc, and the marks turning on it. */
function paintGround(b: Buf, r: Recipe, p: number, f: number, box: Box): void {
  const cx = box.ax * RES;
  const cy = box.ay * RES;
  const R = r.ring * RES;
  const breathe = 1 + 0.04 * Math.sin(p * TAU);
  // Pool: two hard bands of light (or ink) under the feet.
  if (r.pool > 0) {
    const pr = Math.max(R, 13 * RES) * breathe;
    for (let y = -pr * 0.42; y <= pr * 0.42; y++)
      for (let x = -pr; x <= pr; x++) {
        let d = Math.hypot(x / pr, y / (pr * 0.42));
        if (r.ragged) d *= 1 + 0.22 * (noise(Math.atan2(y, x) * 2 + 9, p * 4, 4, 7) - 0.5);
        if (d > 0.98 * r.pool) continue;
        b.put(cx + x, cy + y, d < 0.55 * r.pool ? r.poolInk[1] : r.poolInk[0]);
      }
  }
  if (r.marks === 'taiji') paintTaiji(b, R, cx, cy, p);
  if (R <= 0) return;
  // The ring: lit where it faces the key light (upper right), and turning dashes inside it.
  const steps = Math.ceil(R * 8);
  for (let k = 0; k < steps; k++) {
    const a = (k / steps) * TAU;
    const x = Math.cos(a);
    const y = Math.sin(a);
    const lit = x > 0.25 && y < -0.2;
    b.over(cx + x * R, cy + y * R * 0.42, lit ? r.litInk : r.ringInk);
    // Inner dashes, turning the other way.
    const ia = a + p * TAU * 0.5;
    if (Math.floor((ia / TAU) * 24 + 48) % 3 === 0)
      b.over(cx + x * R * 0.72, cy + y * R * 0.72 * 0.42, r.ringInk);
  }
  const spin = p * TAU;
  const mark = (n: number, fn: (a: number, k: number) => void) => {
    for (let k = 0; k < n; k++) fn(spin * (r.marks === 'lotus' ? 0.25 : 1) + (k * TAU) / n, k);
  };
  const at = (a: number, rr: number): [number, number] => [
    cx + Math.cos(a) * rr,
    cy + Math.sin(a) * rr * 0.42,
  ];
  switch (r.marks) {
    case 'ticks':
      mark(8, (a) => {
        const [x, y] = at(a, R);
        for (let i = -2; i <= 1; i++) b.over(x + i, y, r.litInk);
        b.over(x, y - 1, r.litInk);
      });
      break;
    case 'lotus':
      // Eight petals opening and closing on the ring.
      mark(8, (a) => {
        const open = 0.7 + 0.3 * Math.sin(p * TAU);
        for (let i = 0; i <= 10; i++) {
          const u = i / 10;
          const rr = R * (0.45 + 0.55 * u * open);
          const half = Math.sin(u * Math.PI) * 3;
          const [x, y] = at(a, rr);
          for (let w = -half; w <= half; w++) {
            const tx = -Math.sin(a) * w;
            const ty = Math.cos(a) * w * 0.42;
            b.over(x + tx, y + ty, u > 0.6 && w > 0 ? r.litInk : r.ringInk);
          }
        }
      });
      break;
    case 'petals':
      mark(10, (a, k) => {
        const [x, y] = at(a + hash(k, 3) * 0.3, R * (0.5 + 0.45 * hash(k, 5)));
        b.over(x, y, r.litInk);
        b.over(x + 1, y, r.ringInk);
        b.over(x + 2, y, r.ringInk);
        b.over(x + 1, y + 1, r.ringInk);
      });
      break;
    case 'swords':
      // Swords lying in the ink, points outward, turning slowly.
      mark(9, (a) => {
        for (let i = 0; i <= 14; i++) {
          const [x, y] = at(a, R * 0.62 + i);
          b.over(x, y, i < 3 ? r.ringInk : r.litInk);
          if (i === 3) {
            const [gx, gy] = [-Math.sin(a) * 2, Math.cos(a) * 0.9];
            b.over(x + gx, y + gy, r.ringInk);
            b.over(x - gx, y - gy, r.ringInk);
          }
        }
      });
      break;
    case 'cracks':
      // Ground cracked from the feet, the glow in them flickering.
      for (let k = 0; k < 7; k++) {
        const hot = hash(k, f, 11) < 0.6;
        let a = (k / 7) * TAU + hash(k, 1) * 0.6;
        let rr = R * 0.25;
        while (rr < R * (0.75 + 0.25 * hash(k, 2))) {
          const [x, y] = at(a, rr);
          b.over(x, y, hot ? r.litInk : r.ringInk);
          rr += 1;
          a += (hash(k, Math.floor(rr), 4) - 0.5) * 0.12;
        }
      }
      break;
    default:
      break;
  }
}

/** The taiji turning on the ground: light and dark halves, each holding a seed of the other. */
function paintTaiji(b: Buf, R: number, cx: number, cy: number, p: number): void {
  const th = -p * TAU;
  const light: Ink = [236, 232, 222, 120];
  const lit: Ink = [255, 246, 226, 150];
  const dark: Ink = [26, 24, 34, 130];
  for (let y = -R * 0.42; y <= R * 0.42; y++)
    for (let x = -R; x <= R; x++) {
      const u = x / R;
      const v = y / (R * 0.42);
      if (u * u + v * v > 1) continue;
      const a = u * Math.cos(th) - v * Math.sin(th);
      const c = u * Math.sin(th) + v * Math.cos(th);
      const d1 = Math.hypot(a, c - 0.5);
      const d2 = Math.hypot(a, c + 0.5);
      let white = a > 0;
      if (d1 < 0.5) white = true;
      if (d2 < 0.5) white = false;
      if (d1 < 0.14) white = false;
      if (d2 < 0.14) white = true;
      b.put(cx + x, cy + y, white ? (u > 0.2 && v < -0.2 ? lit : light) : dark);
    }
}

/** Qi (or smoke) rising behind the body in tongues, in three hard bands. */
function paintRise(b: Buf, r: Recipe, p: number, box: Box): void {
  const { h, w, k } = r.rise!;
  const cx = box.ax * RES;
  const cy = box.ay * RES;
  const PY = 8;
  for (let j = 0; j < b.h; j++) {
    const Y = (cy - j) / RES;
    if (Y < -2 || Y > h) continue;
    const half = w * (1 - 0.5 * Math.max(0, Y / h));
    for (let i = 0; i < b.w; i++) {
      const sway = Math.sin((p * 2 + Y / 22) * TAU) * 1.2;
      const X = (i - cx) / RES + sway;
      const e = 1 - (X / half) ** 2;
      if (e <= 0) continue;
      const v = 1 - Math.max(0, Y) / h;
      const lat = Y / 6 - p * k * PY;
      const n = 0.65 * noise(X / 3.2 + 50, lat, PY, 1) + 0.35 * noise(X / 1.6 + 90, lat * 2, PY * 2, 2);
      const I = Math.sqrt(e) * v ** 0.7 * (0.3 + 1.0 * n) + 0.07 * (X / half);
      if (I >= 0.34) b.put(i, j, r.field[I < 0.5 ? 0 : I < 0.66 ? 1 : 2]);
    }
  }
}

/** The golden bell closed over the body: its shell behind, its lip and the light on it in front. */
function paintBell(back: Buf, front: Buf, r: Recipe, p: number, box: Box): void {
  const cx = box.ax * RES;
  const cy = box.ay * RES;
  const k = 1 + 0.035 * Math.sin(p * TAU);
  const H = 46 * RES * k;
  const lipW = 15 * RES * k;
  const [shell, rim, light] = r.field;
  // Half-width of the bell at height y (0 = lip, 1 = crown): a flared lip and a domed crown.
  const widthAt = (u: number) =>
    lipW *
    (u < 0.85
      ? 0.78 + 0.22 * (1 - u / 0.85) ** 3
      : 0.78 * Math.sqrt(Math.max(0, 1 - ((u - 0.85) / 0.15) ** 2)));
  for (let j = 0; j <= H; j++) {
    const u = j / H;
    const wd = widthAt(u);
    const y = cy - j;
    for (let x = -wd; x <= wd; x++) back.put(cx + x, y, [shell[0], shell[1], shell[2], 34]);
    back.over(cx - wd, y, rim);
    back.over(cx + wd, y, u > 0.3 ? light : rim);
    // Two bands round the waist, as cast on a temple bell.
    if (Math.abs(u - 0.62) < 0.012 || Math.abs(u - 0.2) < 0.012)
      for (let x = -wd; x <= wd; x += 1) back.over(cx + x, y, rim);
  }
  // Lip: an ellipse, its back half behind the body and its front half before it.
  const steps = Math.ceil(lipW * 8);
  for (let s = 0; s < steps; s++) {
    const a = (s / steps) * TAU;
    const x = cx + Math.cos(a) * lipW;
    const y = cy + Math.sin(a) * lipW * 0.38;
    if (Math.sin(a) > 0) front.over(x, y, Math.cos(a) > 0.3 ? light : rim);
    else back.over(x, y, rim);
  }
  // The key light's sheen down the right shoulder of the bell, in front.
  for (let j = Math.round(H * 0.35); j < H * 0.85; j++) {
    const wd = widthAt(j / H);
    front.over(cx + wd * 0.62, cy - j, [light[0], light[1], light[2], 90]);
  }
}

/** Embers, motes, petals, swords and cloud round the body: behind it when far, before it when near. */
function paintMotes(back: Buf, front: Buf, r: Recipe, p: number, f: number, box: Box): void {
  const m = r.motes!;
  const cx = box.ax * RES;
  const cy = box.ay * RES;
  const [main, bright] = r.moteInk;
  for (let k = 0; k < m.n; k++) {
    const a0 = hash(k, 21) * TAU;
    const rr = m.r * (0.55 + 0.45 * hash(k, 22));
    const h0 = hash(k, 23);
    const speed = 1 + Math.floor(hash(k, 24) * 2);
    switch (m.kind) {
      case 'rise':
      case 'ember': {
        const ember = m.kind === 'ember';
        const u = (h0 + p * speed) % 1;
        const a = a0 + p * TAU * (ember ? 0 : 1);
        const x = Math.cos(a) * rr * (ember ? 1 - u * 0.5 : 1);
        const y = Math.sin(a) * rr * 0.42 - u * m.h;
        const near = Math.sin(a) > 0;
        const bf = near ? front : back;
        // Fading as it climbs: bright, then the colour, then gone.
        if (u > 0.9) break;
        const c = u < 0.45 ? bright : main;
        const flick = ember && hash(k, f, 25) < 0.25;
        if (flick) break;
        bf.over(cx + x * RES, cy + y * RES, c);
        if (hash(k, 26) < 0.4 && u < 0.6) {
          bf.over(cx + x * RES + 1, cy + y * RES, c);
          bf.over(cx + x * RES, cy + y * RES + 1, main);
        }
        break;
      }
      case 'fall': {
        // Petals drifting down, turning as they fall.
        const u = (h0 + p * speed) % 1;
        const a = a0;
        const x = Math.cos(a) * rr + Math.sin((u * 2 + h0) * TAU) * 3;
        const y = Math.sin(a) * rr * 0.42 - (1 - u) * m.h;
        const bf = Math.sin(a) > 0 ? front : back;
        if (u > 0.95) break;
        const flat = Math.floor((u * 8 + k) % 2) === 0;
        const px = cx + x * RES;
        const py = cy + y * RES;
        bf.over(px, py, bright);
        bf.over(px + 1, py, main);
        if (flat) bf.over(px + 2, py + 1, main);
        else bf.over(px, py + 1, main);
        break;
      }
      case 'blades': {
        // Short swords circling at the waist, points leading.
        const a = (k / m.n) * TAU + p * TAU;
        const bf = Math.sin(a) > 0 ? front : back;
        const tx = -Math.sin(a);
        const ty = Math.cos(a) * 0.42;
        const len = Math.hypot(tx, ty) || 1;
        const ux = tx / len;
        const uy = ty / len;
        const ox = cx + Math.cos(a) * m.r * RES;
        const oy = cy + (Math.sin(a) * m.r * 0.42 - m.h) * RES;
        for (let i = -4; i <= 9; i++) {
          const c: Ink = i < -1 ? [92, 64, 44, 230] : i === -1 ? [176, 142, 70, 240] : i > 6 ? bright : main;
          bf.over(ox + ux * i, oy + uy * i, c);
          // The ridge catches the light on its upper edge.
          if (i >= 0 && i <= 7) bf.over(ox + ux * i, oy + uy * i - 1, i > 3 ? bright : main);
        }
        break;
      }
      case 'mist': {
        // Cloud wreathing the feet: puffs lit on top-right, cool underneath.
        const a = a0 + p * TAU;
        const x = Math.cos(a) * rr;
        const y = Math.sin(a) * rr * 0.42 - 1;
        const pr = (3 + hash(k, 27) * 2.5) * RES;
        const bf = Math.sin(a) > 0 ? front : back;
        const [deep, mid, hi] = r.field;
        for (let j = -pr; j <= pr * 0.7; j++)
          for (let i = -pr; i <= pr; i++) {
            if (i * i + j * j > pr * pr) continue;
            const lit = (i - j) / (pr * 1.41);
            bf.over(
              cx + x * RES + i,
              cy + y * RES + j,
              lit > 0.35
                ? [hi[0], hi[1], hi[2], 170]
                : lit < -0.3
                  ? [deep[0], deep[1], deep[2], 140]
                  : [mid[0], mid[1], mid[2], 150],
            );
          }
        break;
      }
    }
  }
}
